import { Classes, Drawer, Spinner } from "@blueprintjs/core";
import classnames from "classnames";
import React, { useState } from "react";
import { Loading } from "types/loadingType";
import {
    formatFuelTypeForBusPage,
    formatPhaseStatus,
    NOT_IN_SCOPE_LABEL,
    RelatedQueueProjectRow,
    RelatedQueueProjectsTable
} from "types/queueProjectRelatednessType";
import { BusPageFuelTypeIcon } from "./BusPageFuelTypeIcon";
import "./DfaxRelatednessSection.scss";
import { NotInScopeLegendTag, ScoreBucketTag } from "./ScoreBucketTag";

/**
 * Whether the selected scope's inclusion roster can tell us which related
 * projects are in it. "untrackedScope" is surfaced to the user with a notice;
 * "untrackedRegion" is silent.
 */
export type CurrentScopeMembership =
    | "tracked"
    | "untrackedScope"
    | "untrackedRegion";

/**
 * Renders the number of network hops between the study bus and a related
 * project inside a minimal tag so it lines up visually with the Impact tag
 * column. Projects with no hops entry show an em-dash.
 */
const HopsTag: React.FC<{ readonly numHops: number | undefined }> = ({
    numHops
}) => {
    if (numHops === undefined) {
        return <span className="bp5-text-muted">—</span>;
    }
    return <span>{numHops}</span>;
};

const DetailRow: React.FC<{
    readonly label: string;
    readonly value: React.ReactNode;
}> = ({ label, value }) => {
    return (
        <div className="DfaxRelatednessSection-detail-row">
            <span className="bp5-text-muted">{label}</span>
            <span>{value}</span>
        </div>
    );
};

/**
 * Renders a boolean scope-membership flag. While the membership query
 * is still loading the set will be undefined and we show an em-dash so
 * the row reserves space without claiming a yes/no answer it doesn't
 * have yet.
 */
const formatMembership = (
    projectNumber: string,
    projectNumbersInScope: ReadonlySet<string> | undefined
): string => {
    if (projectNumbersInScope === undefined) {
        return "—";
    }
    return projectNumbersInScope.has(projectNumber) ? "Yes" : "No";
};

const ProjectDetailDrawer: React.FC<{
    readonly project: RelatedQueueProjectRow | undefined;
    readonly onClose: () => void;
    readonly isMisoBus: boolean;
    readonly hasImpact: boolean;
    readonly inCurrentScopeProjectNumbers: ReadonlySet<string> | undefined;
    readonly inMisoRealisticProjectNumbers: ReadonlySet<string> | undefined;
    readonly isCurrentScopeMisoRealistic: boolean;
    readonly currentScopeMembership: CurrentScopeMembership;
}> = ({
    project,
    onClose,
    isMisoBus,
    hasImpact,
    inCurrentScopeProjectNumbers,
    inMisoRealisticProjectNumbers,
    isCurrentScopeMisoRealistic,
    currentScopeMembership
}) => {
    if (project === undefined) {
        return null;
    }

    return (
        <Drawer
            isOpen={true}
            onClose={onClose}
            title={project.projectNumber}
            size="360px"
        >
            <div className="DfaxRelatednessSection-drawer-body">
                <DetailRow label="Project #" value={project.projectNumber} />
                <DetailRow label="POI" value={project.poiDescription} />
                <DetailRow
                    label="Fuel Type"
                    value={formatFuelTypeForBusPage(project.fuelType)}
                />
                <DetailRow label="Sub Region" value={project.subRegion} />
                <DetailRow label="Cluster" value={project.cluster ?? "—"} />
                <DetailRow
                    label="Phase"
                    value={
                        project.status !== undefined
                            ? formatPhaseStatus(project.status)
                            : "—"
                    }
                />
                <DetailRow label="Bus ID" value={project.busId ?? "—"} />
                <DetailRow
                    label="Facility ID"
                    value={project.facilityId ?? "—"}
                />
                <DetailRow
                    label="ERIS (MW)"
                    value={project.erisCapacityMw.toFixed(0)}
                />
                <DetailRow
                    label="NRIS (MW)"
                    value={
                        project.nrisCapacityMw !== undefined
                            ? project.nrisCapacityMw.toFixed(0)
                            : "—"
                    }
                />
                {/* Impact is MISO-only. `hasImpact` mirrors the table's
                    Impact column visibility so the drawer never shows an
                    Impact row the table doesn't have. */}
                {hasImpact && (
                    <DetailRow
                        label="Impact"
                        value={<ScoreBucketTag score={project.score} />}
                    />
                )}
                <DetailRow
                    label="Bus Hops"
                    value={<HopsTag numHops={project.numHops} />}
                />
                {/* "Included in current scope" is redundant when the user's
                    current scope IS MISO Realistic, so we hide it in that
                    case to avoid a duplicate-looking row. */}
                {currentScopeMembership === "tracked" &&
                    !isCurrentScopeMisoRealistic && (
                        <DetailRow
                            label="Included in current scope"
                            value={formatMembership(
                                project.projectNumber,
                                inCurrentScopeProjectNumbers
                            )}
                        />
                    )}
                {isMisoBus && (
                    <DetailRow
                        label="Included in MISO Realistic"
                        value={formatMembership(
                            project.projectNumber,
                            inMisoRealisticProjectNumbers
                        )}
                    />
                )}
            </div>
        </Drawer>
    );
};

const ProjectRow: React.FC<{
    readonly project: RelatedQueueProjectRow;
    readonly showImpact: boolean;
    readonly showHops: boolean;
    // undefined = membership unknown (query loading, errored, or lookup
    // not deployed); distinct from a definitive in/out answer.
    readonly isInCurrentScope: boolean | undefined;
    // True only while the membership query is in flight, so skeletons
    // never render indefinitely when membership is simply unavailable.
    readonly isMembershipLoading: boolean;
    readonly onSelect: () => void;
}> = ({
    project,
    showImpact,
    showHops,
    isInCurrentScope,
    isMembershipLoading,
    onSelect
}) => {
    // Fuel type is conveyed by the leading icon, so the subtitle only
    // carries cluster and phase.
    const subtitle = [
        project.cluster,
        project.status !== undefined
            ? formatPhaseStatus(project.status)
            : undefined
    ]
        .filter((s): s is string => s !== undefined && s.length > 0)
        .join(" · ");

    return (
        <tr className="DfaxRelatednessSection-project-row" onClick={onSelect}>
            <td className="DfaxRelatednessSection-project-td">
                <div className="DfaxRelatednessSection-project-cell">
                    <BusPageFuelTypeIcon fuelType={project.fuelType} />
                    <div className="DfaxRelatednessSection-project-text">
                        <span className="DfaxRelatednessSection-title-row">
                            <span className="DfaxRelatednessSection-project-link">
                                {project.projectNumber}
                            </span>
                            {isMembershipLoading ? (
                                <span
                                    className={classnames(
                                        Classes.SKELETON,
                                        "DfaxRelatednessSection-scope-skeleton"
                                    )}
                                />
                            ) : (
                                isInCurrentScope === false && (
                                    <span className="DfaxRelatednessSection-scope-label">
                                        ({NOT_IN_SCOPE_LABEL.toLowerCase()})
                                    </span>
                                )
                            )}
                        </span>
                        <span className="bp5-text-muted DfaxRelatednessSection-project-subtitle">
                            {subtitle}
                        </span>
                    </div>
                </div>
            </td>
            {showImpact && (
                <td className="DfaxRelatednessSection-score-cell">
                    {isMembershipLoading ? (
                        <span
                            className={classnames(
                                Classes.SKELETON,
                                "DfaxRelatednessSection-tag-skeleton"
                            )}
                        />
                    ) : (
                        <ScoreBucketTag
                            score={project.score}
                            notInScope={isInCurrentScope === false}
                        />
                    )}
                </td>
            )}
            {showHops && (
                <td className="DfaxRelatednessSection-hops-cell">
                    <HopsTag numHops={project.numHops} />
                </td>
            )}
        </tr>
    );
};

const DfaxRelatednessSection: React.FC<{
    readonly table: Loading<RelatedQueueProjectsTable>;
    readonly currentScopeDisplayName: string;
    readonly isMisoBus: boolean;
    readonly inCurrentScopeProjectNumbers: ReadonlySet<string> | undefined;
    readonly inMisoRealisticProjectNumbers: ReadonlySet<string> | undefined;
    readonly isCurrentScopeMisoRealistic: boolean;
    readonly currentScopeMembership: CurrentScopeMembership;
    readonly isScopeMembershipLoading: boolean;
}> = ({
    table,
    currentScopeDisplayName,
    isMisoBus,
    inCurrentScopeProjectNumbers,
    inMisoRealisticProjectNumbers,
    isCurrentScopeMisoRealistic,
    currentScopeMembership,
    isScopeMembershipLoading
}) => {
    const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
        undefined
    );
    const scopeMembershipNotice = currentScopeMembership ===
        "untrackedScope" && (
        <div className="DfaxRelatednessSection-legend">
            <span className="bp5-text-muted DfaxRelatednessSection-legend-text">
                For {currentScopeDisplayName}, this table does not indicate
                which projects are included in the scope.
            </span>
        </div>
    );

    if (table === "loading") {
        return (
            <div className="DfaxRelatednessSection">
                <Heading
                    hasImpact={true}
                    currentScopeMembership={currentScopeMembership}
                />
                <div className="DfaxRelatednessSection-loading">
                    <Spinner size={24} />
                </div>
                {scopeMembershipNotice}
            </div>
        );
    }

    const { rows, hasImpact, hasHops } = table;

    if (rows.length === 0) {
        return (
            <div className="DfaxRelatednessSection">
                <Heading
                    hasImpact={hasImpact}
                    currentScopeMembership={currentScopeMembership}
                />
                <div className="bp5-text-muted DfaxRelatednessSection-empty">
                    No related queue projects.
                </div>
                {scopeMembershipNotice}
            </div>
        );
    }

    const selectedProject =
        selectedIndex !== undefined ? rows[selectedIndex] : undefined;

    // The scope-membership legend only renders once membership is known
    // AND at least one visible project is out of scope: when every row is
    // dispatched (or membership is unavailable) there is no dashed tag or
    // "Not in scope" label on screen for it to explain.
    const hasNotInScopeRows =
        inCurrentScopeProjectNumbers !== undefined &&
        rows.some(
            (project) =>
                !inCurrentScopeProjectNumbers.has(project.projectNumber)
        );

    return (
        <div className="DfaxRelatednessSection">
            <Heading
                hasImpact={hasImpact}
                currentScopeMembership={currentScopeMembership}
            />
            <div className="DfaxRelatednessSection-table-wrapper">
                <table className="bp5-html-table bp5-html-table-condensed DfaxRelatednessSection-table">
                    <thead>
                        <tr>
                            <th className="DfaxRelatednessSection-th">
                                Project
                            </th>
                            {hasImpact && (
                                <th className="DfaxRelatednessSection-th DfaxRelatednessSection-score-th">
                                    Impact
                                </th>
                            )}
                            {hasHops && (
                                <th className="DfaxRelatednessSection-th DfaxRelatednessSection-hops-th">
                                    Hops
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((project, index) => (
                            <ProjectRow
                                key={project.projectNumber}
                                project={project}
                                showImpact={hasImpact}
                                showHops={hasHops}
                                isInCurrentScope={inCurrentScopeProjectNumbers?.has(
                                    project.projectNumber
                                )}
                                isMembershipLoading={isScopeMembershipLoading}
                                onSelect={() => setSelectedIndex(index)}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
            {scopeMembershipNotice}
            {hasImpact && hasNotInScopeRows && (
                <div className="DfaxRelatednessSection-legend">
                    <NotInScopeLegendTag />
                    <span className="bp5-text-muted DfaxRelatednessSection-legend-text">
                        shows the impact a project would have if dispatched. The
                        project&rsquo;s generation is not in the loadings shown.
                    </span>
                </div>
            )}
            <ProjectDetailDrawer
                project={selectedProject}
                onClose={() => setSelectedIndex(undefined)}
                isMisoBus={isMisoBus}
                hasImpact={hasImpact}
                inCurrentScopeProjectNumbers={inCurrentScopeProjectNumbers}
                inMisoRealisticProjectNumbers={inMisoRealisticProjectNumbers}
                isCurrentScopeMisoRealistic={isCurrentScopeMisoRealistic}
                currentScopeMembership={currentScopeMembership}
            />
        </div>
    );
};

const Heading: React.FC<{
    readonly hasImpact: boolean;
    readonly currentScopeMembership: CurrentScopeMembership;
}> = ({ hasImpact, currentScopeMembership }) => {
    return (
        <>
            <h5 className="bp5-heading DfaxRelatednessSection-heading">
                Related Queue Projects
            </h5>
            {hasImpact ? (
                <>
                    <p className="bp5-text-muted DfaxRelatednessSection-subtitle">
                        Ranked by their contribution (impact) to the constraints
                        you hit
                        {currentScopeMembership === "untrackedScope"
                            ? " in MISO Realistic"
                            : ""}{" "}
                        at your current project size.
                    </p>
                    <p className="bp5-text-muted DfaxRelatednessSection-subtitle">
                        Changing the project size changes which constraints you
                        hit — and therefore which queue projects appear here and
                        how they rank.
                    </p>
                </>
            ) : (
                <p className="bp5-text-muted DfaxRelatednessSection-subtitle">
                    Projects near this bus on the network, ranked by network
                    distance (bus hops), closest first.
                </p>
            )}
        </>
    );
};

export default DfaxRelatednessSection;
